import {
  Calendar,
  CalendarPlus,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  LayoutList,
  Pencil,
  RefreshCw,
  Trash2,
  Users,
  Video,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Link } from "react-router-dom";
import { CalendarEventLocation } from "@/components/CalendarEventLocation";
import { ConnectionBrandIcon } from "@/components/ConnectionBrandIcon";
import { ModuleDrawerAdd } from "@/components/ModuleDrawerAdd";
import {
  descriptionLooksLikeHtml,
  resolveEventPlace,
  sanitizeEventHtml,
} from "@/lib/calendarEventPlace";
import { ApiError, api } from "@/lib/api";
import { PaneResizer } from "@/components/PaneResizer";
import {
  formatCalendarSourceLabel,
  formatCalendarSourceTitle,
  formatEventCalendarKicker,
  pickCalendarName,
  type CalendarNameFields,
} from "@/lib/calendarLabels";
import {
  defaultDeclineAlso,
  isEventOrganizer,
  viewerResponseStatus,
  type CalAttendeeLike,
  type CalOrganizerLike,
} from "@/lib/calendarEventRole";
import { matchesHubSearch, useDebouncedValue, useHubSearchQuery } from "@/lib/hubSearch";
import {
  moduleCacheAge,
  peekModuleCache,
  pruneModuleCache,
  readModuleCache,
  writeModuleCache,
} from "@/lib/moduleCache";
import { useLiveRefresh } from "@/lib/useLiveRefresh";

const CALENDAR_CACHE_PREFIX = "cal:";
const CALENDAR_RANGES_CACHED = 36;
const CALENDAR_REFRESH_MS = 60_000;
const CALENDAR_PREFETCH_FRESH_MS = 5 * 60_000;

type CalAttendee = CalAttendeeLike;

type CalReminder = {
  method?: string;
  minutes?: number;
};

type CalEvent = {
  id: string;
  title?: string;
  summary?: string;
  start?: string;
  end?: string;
  calendarId?: string;
  /** V26.1 live API calendar display name */
  calendar?: string;
  calendarName?: string;
  accountId?: string;
  accountEmail?: string;
  color?: string;
  location?: string;
  description?: string;
  notes?: string;
  allDay?: boolean;
  /** Tip 138: organizer / creator / viewer RSVP from Google/Outlook passthrough */
  organizer?: string | CalOrganizerLike;
  organizerEmail?: string;
  creator?: string | CalOrganizerLike;
  isOrganizer?: boolean;
  self?: boolean;
  /** Event-level viewer RSVP (accepted / tentative / declined / …) */
  responseStatus?: string;
  attendees?: Array<string | CalAttendee>;
  guests?: Array<string | CalAttendee>;
  meetLink?: string;
  hangoutLink?: string;
  conferenceLink?: string;
  htmlLink?: string;
  recurrence?: string | string[];
  recurrenceSummary?: string;
  recurringEventId?: string;
  reminders?: CalReminder[] | { overrides?: CalReminder[]; useDefault?: boolean };
  reminderMinutes?: number[];
};

type CalEventDraft = {
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  location: string;
  description: string;
};

/** Raw calendar row from `/api/calendar` `sources` (or `calendars`). */
type ApiCalSource = {
  accountId?: string;
  accountEmail?: string;
  calendarId?: string;
  id?: string;
  name?: string;
  summary?: string;
  displayName?: string;
  calendarName?: string;
  color?: string;
  backgroundColor?: string;
  accessRole?: string;
};

type CalSource = {
  key: string;
  accountId: string;
  accountEmail: string;
  calendarId: string;
  name: string;
  label: string;
  title: string;
  color: string;
};

type CalAccountGroup = {
  accountId: string;
  accountEmail: string;
  calendars: CalSource[];
};

type CalendarPayload = {
  events?: CalEvent[];
  sources?: ApiCalSource[];
  calendars?: ApiCalSource[];
};

type TimedLayout = {
  event: CalEvent;
  column: number;
  columnCount: number;
};

const VIEW_OPTIONS = [
  { id: "1day", label: "1 Day" },
  { id: "3day", label: "3 Day" },
  { id: "workweek", label: "Work Week" },
  { id: "week", label: "Week" },
  { id: "2weeks", label: "2 Weeks" },
  { id: "month", label: "Month" },
] as const;

type ViewId = (typeof VIEW_OPTIONS)[number]["id"];

const HIDDEN_KEY = "atw-hub-cal-hidden-sources";
const HIDDEN_ACCOUNTS_KEY = "atw-hub-cal-hidden-accounts";
const WEEK_START_KEY = "atw-hub-cal-week-start";
const PHONE_VIEW_KEY = "atw-hub-cal-view-phone";
const DESKTOP_VIEW_KEY = "atw-hub-cal-view-desktop";
const PHONE_VIEW_QUERY = "(max-width: 980px)";
/** Phone: show either the grid or the agenda list — not both (tip 131). */
const PHONE_PANE_KEY = "atw-hub-cal-phone-pane";
const DETAIL_SPLIT_KEY = "atw-hub-cal-detail-split";
const DETAIL_SPLIT_DEFAULT = 50;
const DETAIL_SPLIT_MIN = 28;
const DETAIL_SPLIT_MAX = 72;

type PhonePaneId = "calendar" | "list";

function clampDetailSplit(value: number): number {
  return Math.min(DETAIL_SPLIT_MAX, Math.max(DETAIL_SPLIT_MIN, Math.round(value)));
}

function readDetailSplit(): number {
  try {
    const raw = Number(localStorage.getItem(DETAIL_SPLIT_KEY));
    if (Number.isFinite(raw)) return clampDetailSplit(raw);
  } catch {
    /* ignore */
  }
  return DETAIL_SPLIT_DEFAULT;
}

/** Match tip 135 CSS: 100dvh − top bar − phone tab chrome. */
function phoneCalAvailHeight(): number {
  let tab = 52;
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue("--phone-tab-h").trim();
    const n = Number.parseFloat(raw);
    if (Number.isFinite(n) && n > 0) tab = n;
  } catch {
    /* ignore */
  }
  const safe =
    typeof window !== "undefined" && window.visualViewport
      ? Math.max(0, window.innerHeight - window.visualViewport.height)
      : 0;
  return Math.max(160, window.innerHeight - 62 - tab - Math.min(safe, 40));
}

function readPhonePane(): PhonePaneId {
  try {
    const value = localStorage.getItem(PHONE_PANE_KEY);
    if (value === "list" || value === "calendar") return value;
  } catch {
    /* ignore */
  }
  return "calendar";
}
const HOUR_PX = 52;
const MIN_EVENT_MS = 15 * 60 * 1000;

const PALETTE = ["#7eb6d9", "#8fbf88", "#c4a3e0", "#e8a0a0", "#f0c674", "#6a8f9e", "#d4a574", "#9aa7b5"];

function isPhoneCalendar(): boolean {
  return window.matchMedia(PHONE_VIEW_QUERY).matches;
}

function isViewId(value: string | null): value is ViewId {
  return VIEW_OPTIONS.some((opt) => opt.id === value);
}

function readSavedView(): ViewId {
  const phone = isPhoneCalendar();
  const fallback: ViewId = phone ? "3day" : "week";
  try {
    const raw = localStorage.getItem(phone ? PHONE_VIEW_KEY : DESKTOP_VIEW_KEY);
    return isViewId(raw) ? raw : fallback;
  } catch {
    return fallback;
  }
}

function saveSavedView(next: ViewId) {
  try {
    localStorage.setItem(isPhoneCalendar() ? PHONE_VIEW_KEY : DESKTOP_VIEW_KEY, next);
  } catch {
    /* ignore */
  }
}

function readWeekStart(): number {
  try {
    const n = Number.parseInt(localStorage.getItem(WEEK_START_KEY) || "1", 10);
    return Number.isInteger(n) && n >= 0 && n <= 6 ? n : 1;
  } catch {
    return 1;
  }
}

function startOfWeek(d: Date, weekStart = readWeekStart()) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  const start = ((weekStart % 7) + 7) % 7;
  const delta = (day - start + 7) % 7;
  x.setDate(x.getDate() - delta);
  return x;
}

function addDays(d: Date, n: number) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function sameDay(a: Date | string, b: Date) {
  return new Date(a).toDateString() === b.toDateString();
}

function formatRange(from: Date, to: Date) {
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const yOpts: Intl.DateTimeFormatOptions = { ...opts, year: "numeric" };
  return `${from.toLocaleDateString("en-US", opts)} to ${to.toLocaleDateString("en-US", yOpts)}`;
}

function formatEventWhen(start?: string, end?: string, allDay?: boolean) {
  if (!start) return "";
  if (allDay || isAllDayEvent({ start, end, allDay })) {
    const s = new Date(start);
    if (Number.isNaN(s.getTime())) return start;
    return `${s.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    })} · All day`;
  }
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  if (Number.isNaN(s.getTime())) return start;
  const day = s.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const t0 = s.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (!e || Number.isNaN(e.getTime())) return `${day}, ${t0}`;
  const t1 = e.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${day}, ${t0} to ${t1}`;
}

function formatHourLabel(hour: number) {
  return new Date(2000, 0, 1, hour).toLocaleTimeString("en-US", { hour: "numeric" });
}

function formatShortTime(iso?: string) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function colorFor(sourceKey: string, explicit?: string) {
  if (explicit) return explicit;
  let hash = 0;
  for (let i = 0; i < sourceKey.length; i++) hash = (hash * 31 + sourceKey.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

/** Soft fill from hex + left border accent (Outlook / prior Hub tip). */
function softColor(hex: string, alpha = 0.28) {
  const raw = hex.replace("#", "");
  if (raw.length !== 6) return hex;
  const r = Number.parseInt(raw.slice(0, 2), 16);
  const g = Number.parseInt(raw.slice(2, 4), 16);
  const b = Number.parseInt(raw.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return hex;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function loadSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveSet(key: string, set: Set<string>) {
  try {
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

function eventCalendarFields(ev: CalEvent): CalendarNameFields {
  // Do not pass ev.summary / ev.title — those are event titles, not calendar names.
  return {
    calendarName: ev.calendarName,
    calendar: ev.calendar,
    accountEmail: ev.accountEmail,
    calendarId: ev.calendarId,
  };
}

function sourceCalendarFields(src: ApiCalSource, accountEmail: string): CalendarNameFields {
  return {
    name: src.name,
    summary: src.summary,
    displayName: src.displayName,
    calendarName: src.calendarName,
    accountEmail: src.accountEmail || accountEmail,
    calendarId: src.calendarId || src.id,
  };
}

function normalizeApiSources(payload: CalendarPayload): ApiCalSource[] {
  const raw = payload.sources ?? payload.calendars ?? [];
  return Array.isArray(raw) ? raw : [];
}

function sourceBaseName(fields: CalendarNameFields): string {
  return pickCalendarName(fields);
}

/**
 * Prefer API `sources` (Google calendar list summaries). Fall back to distinct
 * calendars seen on events so show/hide still works if sources are omitted.
 */
function buildGroups(sources: ApiCalSource[], events: CalEvent[]): CalAccountGroup[] {
  type Draft = {
    key: string;
    accountId: string;
    accountEmail: string;
    calendarId: string;
    fields: CalendarNameFields;
    color: string;
  };
  const drafts: Draft[] = [];
  const seen = new Set<string>();

  for (const src of sources) {
    const accountEmail = (src.accountEmail || "").trim() || "Calendar";
    const accountId = (src.accountId || accountEmail || "default").trim() || "default";
    const calendarId = (src.calendarId || src.id || "primary").trim() || "primary";
    const key = `${accountId}::${calendarId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const fields = sourceCalendarFields(src, accountEmail);
    drafts.push({
      key,
      accountId,
      accountEmail,
      calendarId,
      fields,
      color: colorFor(key, src.color || src.backgroundColor),
    });
  }

  for (const ev of events) {
    const accountEmail = (ev.accountEmail || ev.accountId || "Calendar").trim() || "Calendar";
    const accountId = (ev.accountId || ev.accountEmail || "default").trim() || "default";
    const calendarId = (ev.calendarId || "primary").trim() || "primary";
    const key = `${accountId}::${calendarId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    drafts.push({
      key,
      accountId,
      accountEmail,
      calendarId,
      fields: eventCalendarFields(ev),
      color: colorFor(key, ev.color),
    });
  }

  const nameOwners = new Map<string, Set<string>>();
  for (const d of drafts) {
    const base = sourceBaseName(d.fields) || d.accountEmail;
    const owners = nameOwners.get(base) ?? new Set<string>();
    owners.add(d.accountId);
    nameOwners.set(base, owners);
  }

  const map = new Map<string, CalAccountGroup>();
  for (const d of drafts) {
    const base = sourceBaseName(d.fields);
    const needsDisambiguation = Boolean(
      base && (nameOwners.get(base)?.size ?? 0) > 1,
    );
    const label = formatCalendarSourceLabel(d.fields, needsDisambiguation);
    const title = formatCalendarSourceTitle(d.fields);
    if (!map.has(d.accountId)) {
      map.set(d.accountId, {
        accountId: d.accountId,
        accountEmail: d.accountEmail,
        calendars: [],
      });
    }
    map.get(d.accountId)!.calendars.push({
      key: d.key,
      accountId: d.accountId,
      accountEmail: d.accountEmail,
      calendarId: d.calendarId,
      name: base || d.accountEmail,
      label,
      title,
      color: d.color,
    });
  }

  return [...map.values()]
    .sort((a, b) => a.accountEmail.localeCompare(b.accountEmail, "en", { sensitivity: "base" }))
    .map((group) => ({
      ...group,
      calendars: [...group.calendars].sort((a, b) =>
        a.label.localeCompare(b.label, "en", { sensitivity: "base" }),
      ),
    }));
}

function resolveEventCalendarFields(
  ev: CalEvent,
  groups: CalAccountGroup[],
): CalendarNameFields {
  const key = sourceKey(ev);
  for (const group of groups) {
    const match = group.calendars.find((c) => c.key === key);
    if (match) {
      const named = match.name && match.name !== match.accountEmail ? match.name : undefined;
      return {
        name: named,
        calendarName: named,
        accountEmail: match.accountEmail || ev.accountEmail,
        calendarId: match.calendarId,
      };
    }
  }
  return eventCalendarFields(ev);
}

function viewSpanDays(view: ViewId): number {
  switch (view) {
    case "1day":
      return 1;
    case "3day":
      return 3;
    case "workweek":
      return 5;
    case "2weeks":
      return 14;
    case "month":
      return 28;
    default:
      return 7;
  }
}

type CalendarSnapshot = { events: CalEvent[]; sources: ApiCalSource[] };

function calendarRange(anchor: Date, view: ViewId, weekStart: number): { from: Date; to: Date } {
  const usesWeek = view === "week" || view === "workweek" || view === "2weeks" || view === "month";
  const from = usesWeek ? startOfWeek(anchor, weekStart) : new Date(anchor);
  from.setHours(0, 0, 0, 0);
  if (view === "month") {
    const monthStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(monthStart, weekStart);
    const monthEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    const gridEnd = addDays(startOfWeek(monthEnd, weekStart), 7);
    return { from: gridStart, to: gridEnd };
  }
  return { from, to: addDays(from, viewSpanDays(view)) };
}

function calendarCacheKey(range: { from: Date; to: Date }): string {
  return `${CALENDAR_CACHE_PREFIX}${range.from.toISOString()}:${range.to.toISOString()}`;
}

function shiftAnchor(anchor: Date, view: ViewId, dir: -1 | 1): Date {
  if (view === "month") return new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1);
  return addDays(anchor, dir * viewSpanDays(view));
}

async function fetchCalendarSnapshot(from: Date, to: Date, search?: string): Promise<CalendarSnapshot> {
  const query = search ? `&q=${encodeURIComponent(search)}` : "";
  const payload = await api<CalendarPayload>(
    `/api/calendar?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}${query}`,
  );
  return {
    events: (payload.events || []).map(normalizeCalendarEvent),
    sources: normalizeApiSources(payload),
  };
}

/** Save the previous and next range quietly so arrowing through the calendar is instant. */
async function prefetchNeighborRanges(anchor: Date, view: ViewId, weekStart: number): Promise<void> {
  for (const dir of [1, -1] as const) {
    const range = calendarRange(shiftAnchor(anchor, view, dir), view, weekStart);
    const key = calendarCacheKey(range);
    const age = moduleCacheAge(key);
    if (age != null && age < CALENDAR_PREFETCH_FRESH_MS) continue;
    try {
      await writeModuleCache(key, await fetchCalendarSnapshot(range.from, range.to), { mirror: true });
    } catch {
      /* the next visit loads it live */
    }
  }
}

/**
 * Dates on screen for this app session: leaving Calendar and coming back keeps them;
 * a cold start, reopen, or page refresh resets to null so Calendar opens on today.
 */
let sessionAnchor: Date | null = null;

/** The anchor that shows `date`: its day, its month, or the week containing it. */
function anchorForDate(view: ViewId, weekStart: number, date: Date): Date {
  const day = new Date(date);
  if (view === "month") return new Date(day.getFullYear(), day.getMonth(), 1);
  if (view === "1day" || view === "3day") {
    day.setHours(0, 0, 0, 0);
    return day;
  }
  return startOfWeek(day, weekStart);
}

/** Where Today goes. */
function todayAnchor(view: ViewId, weekStart: number): Date {
  return anchorForDate(view, weekStart, new Date());
}

/** How far around today the Calendar search looks. */
const CALENDAR_SEARCH_BACK_DAYS = 90;
const CALENDAR_SEARCH_AHEAD_DAYS = 365;

function calendarEventMatchesSearch(ev: CalEvent, query: string): boolean {
  return matchesHubSearch(
    query,
    eventTitle(ev),
    ev.location,
    ev.description,
    ev.notes,
    ev.calendarName,
    ev.accountEmail,
    ev.organizer,
    ev.organizerEmail,
    ev.attendees,
    ev.guests,
  );
}

function initialAnchor(): Date {
  return sessionAnchor ? new Date(sessionAnchor) : todayAnchor(readSavedView(), readWeekStart());
}

/** Saved events for the range the calendar opens on, for a spinner-free first paint. */
function peekInitialCalendar(): CalendarSnapshot | null {
  const weekStart = readWeekStart();
  const range = calendarRange(initialAnchor(), readSavedView(), weekStart);
  return peekModuleCache<CalendarSnapshot>(calendarCacheKey(range));
}

function isDateOnly(iso?: string) {
  return Boolean(iso && /^\d{4}-\d{2}-\d{2}$/.test(iso));
}

function isAllDayEvent(ev: Pick<CalEvent, "start" | "end" | "allDay">) {
  if (ev.allDay) return true;
  if (isDateOnly(ev.start) || isDateOnly(ev.end)) return true;
  if (!ev.start || !ev.end) return false;
  const s = new Date(ev.start);
  const e = new Date(ev.end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return false;
  const overnight =
    e.getTime() - s.getTime() >= 24 * 60 * 60 * 1000 &&
    s.getHours() === 0 &&
    s.getMinutes() === 0 &&
    e.getHours() === 0 &&
    e.getMinutes() === 0;
  return overnight;
}

function eventTitle(ev: CalEvent) {
  return ev.title || ev.summary || "(no title)";
}

function sourceKey(ev: CalEvent) {
  const accountId = ev.accountId || ev.accountEmail || "default";
  const calendarId = ev.calendarId || "primary";
  return `${accountId}::${calendarId}`;
}

function pickString(...vals: unknown[]): string {
  for (const v of vals) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

function eventDescription(ev: CalEvent) {
  return pickString(ev.description, ev.notes);
}

function eventMeetLink(ev: CalEvent) {
  return pickString(ev.meetLink, ev.hangoutLink, ev.conferenceLink);
}

function formatAttendee(entry: string | CalAttendee): string {
  if (typeof entry === "string") return entry.trim();
  const name = pickString(entry.displayName, entry.name);
  const email = pickString(entry.email);
  if (name && email && name !== email) return `${name} <${email}>`;
  return name || email || "";
}

function eventAttendees(ev: CalEvent): string[] {
  const raw = ev.attendees || ev.guests || [];
  return raw.map(formatAttendee).filter(Boolean);
}

function eventRecurrenceSummary(ev: CalEvent): string {
  const summary = pickString(ev.recurrenceSummary);
  if (summary) return summary;
  if (typeof ev.recurrence === "string" && ev.recurrence.trim()) return ev.recurrence.trim();
  if (Array.isArray(ev.recurrence) && ev.recurrence.length) {
    return ev.recurrence.map((r) => String(r)).filter(Boolean).join("; ");
  }
  if (ev.recurringEventId) return "Repeating event";
  return "";
}

function eventReminderLines(ev: CalEvent): string[] {
  if (Array.isArray(ev.reminderMinutes) && ev.reminderMinutes.length) {
    return ev.reminderMinutes.map((m) => `${m} min before`);
  }
  const bag = ev.reminders;
  if (!bag) return [];
  const overrides = Array.isArray(bag) ? bag : bag.overrides || [];
  return overrides
    .map((r) => {
      const mins = typeof r.minutes === "number" ? r.minutes : null;
      if (mins == null) return "";
      const method = pickString(r.method);
      return method ? `${mins} min before (${method})` : `${mins} min before`;
    })
    .filter(Boolean);
}

function toDatetimeLocalValue(iso?: string): string {
  if (!iso) return "";
  if (isDateOnly(iso)) return `${iso}T09:00`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(local: string): string {
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return local;
  return d.toISOString();
}

function draftFromEvent(ev: CalEvent): CalEventDraft {
  return {
    title: eventTitle(ev) === "(no title)" ? "" : eventTitle(ev),
    start: toDatetimeLocalValue(ev.start),
    end: toDatetimeLocalValue(ev.end || ev.start),
    allDay: isAllDayEvent(ev),
    location: pickString(ev.location),
    description: eventDescription(ev),
  };
}

function normalizeCalendarEvent(raw: CalEvent): CalEvent {
  const meetLink = eventMeetLink(raw);
  const description = eventDescription(raw);
  const calendarName = pickString(raw.calendarName, raw.calendar);
  return {
    ...raw,
    title: pickString(raw.title, raw.summary) || raw.title,
    description: description || undefined,
    calendarName: calendarName || undefined,
    meetLink: meetLink || undefined,
  };
}

/** Side-by-side columns for overlapping timed events (prior Hub / Outlook). */
function layoutTimedOverlaps(events: CalEvent[]): TimedLayout[] {
  const items = events
    .filter((ev) => !isAllDayEvent(ev) && ev.start)
    .map((ev) => {
      const start = new Date(ev.start!).getTime();
      const endRaw = ev.end ? new Date(ev.end).getTime() : start + MIN_EVENT_MS;
      return {
        event: ev,
        start,
        end: Math.max(endRaw, start + MIN_EVENT_MS),
      };
    })
    .sort((a, b) => a.start - b.start || a.end - b.end);

  if (items.length === 0) return [];

  type Active = { end: number; column: number };
  const placed: Array<{ event: CalEvent; column: number; cluster: number; columnCount: number }> =
    [];
  let cluster = 0;
  let active: Active[] = [];
  let clusterWidth = 0;

  for (const item of items) {
    active = active.filter((a) => a.end > item.start);
    if (active.length === 0) {
      cluster += 1;
      clusterWidth = 0;
    }
    const used = new Set(active.map((a) => a.column));
    let column = 0;
    while (used.has(column)) column += 1;
    active.push({ end: item.end, column });
    clusterWidth = Math.max(clusterWidth, column + 1);
    placed.push({ event: item.event, column, cluster, columnCount: clusterWidth });
    for (const row of placed) {
      if (row.cluster === cluster) row.columnCount = clusterWidth;
    }
  }

  const maxByCluster = new Map<number, number>();
  for (const row of placed) {
    maxByCluster.set(row.cluster, Math.max(maxByCluster.get(row.cluster) ?? 0, row.column + 1));
  }

  return placed.map((row) => ({
    event: row.event,
    column: row.column,
    columnCount: maxByCluster.get(row.cluster) ?? 1,
  }));
}

function hourWindow(events: CalEvent[], now: Date | null) {
  let min = 7;
  let max = 19;
  for (const ev of events) {
    if (isAllDayEvent(ev) || !ev.start) continue;
    const s = new Date(ev.start);
    if (Number.isNaN(s.getTime())) continue;
    min = Math.min(min, s.getHours());
    if (ev.end) {
      const e = new Date(ev.end);
      if (!Number.isNaN(e.getTime())) {
        max = Math.max(max, e.getMinutes() > 0 ? e.getHours() + 1 : e.getHours());
      }
    }
  }
  if (now) {
    const h = now.getHours();
    min = Math.min(min, h);
    max = Math.max(max, Math.min(24, h + 1));
  }
  min = Math.max(0, Math.min(min, 7));
  max = Math.min(24, Math.max(max, 18, min + 1));
  return {
    min,
    max,
    labels: Array.from({ length: max - min }, (_, i) => min + i),
  };
}

function eventTouchesDay(ev: CalEvent, day: Date) {
  if (!ev.start) return false;
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = addDays(dayStart, 1);

  if (isAllDayEvent(ev)) {
    const s = new Date(ev.start);
    if (Number.isNaN(s.getTime())) return false;
    const sDay = new Date(s);
    sDay.setHours(0, 0, 0, 0);
    let eDay = ev.end ? new Date(ev.end) : addDays(sDay, 1);
    if (Number.isNaN(eDay.getTime())) eDay = addDays(sDay, 1);
    // Google all-day end is exclusive date
    if (isDateOnly(ev.end) || (ev.end && eDay.getHours() === 0 && eDay.getMinutes() === 0)) {
      /* keep exclusive */
    } else {
      eDay = addDays(new Date(eDay.getFullYear(), eDay.getMonth(), eDay.getDate()), 1);
    }
    return sDay < dayEnd && eDay > dayStart;
  }

  return sameDay(ev.start, day);
}

/** Tip 139: Google calendar token/scope problems → send user to Connections to reconnect. */
function isCalendarAuthError(err: unknown): boolean {
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) return true;
  const msg = (err instanceof Error ? err.message : String(err || "")).toLowerCase();
  return /unauthorized|forbidden|invalid_grant|token.*(expir|revok|invalid)|access.*(denied|revok)|reauth|reconnect|not authenticated|login required|insufficient.*(scope|permission)/i.test(
    msg,
  );
}

function calendarReconnectPath(email?: string | null): string {
  const base = "/settings/connections/calendar";
  const tip = (email || "").trim();
  if (!tip) return `${base}?reconnect=1`;
  return `${base}?reconnect=${encodeURIComponent(tip)}`;
}

function eventDetailsLookSparse(ev: CalEvent): boolean {
  const place = resolveEventPlace({
    meetLink: ev.meetLink,
    hangoutLink: ev.hangoutLink,
    conferenceLink: ev.conferenceLink,
    location: ev.location,
    description: eventDescription(ev),
  });
  const noLocation = !place.location;
  const noDescription = !eventDescription(ev);
  const noAttendees = eventAttendees(ev).length === 0;
  const noMeet = !place.meeting;
  return noLocation && noDescription && noAttendees && noMeet;
}

export function CalendarPage() {
  const [events, setEvents] = useState<CalEvent[]>(() => peekInitialCalendar()?.events ?? []);
  const [sources, setSources] = useState<ApiCalSource[]>(() => peekInitialCalendar()?.sources ?? []);
  const [error, setError] = useState("");
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(() => !peekInitialCalendar());
  const [view, setView] = useState<ViewId>(readSavedView);
  const [viewOpen, setViewOpen] = useState(false);
  const [weekStart, setWeekStart] = useState(readWeekStart);
  const [anchor, setAnchor] = useState(initialAnchor);

  useEffect(() => {
    sessionAnchor = anchor;
  }, [anchor]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [phonePane, setPhonePane] = useState<PhonePaneId>(readPhonePane);
  const [detailSplitPct, setDetailSplitPct] = useState(readDetailSplit);
  const detailResizeRef = useRef<{ startY: number; startPct: number } | null>(null);
  const [hiddenSources, setHiddenSources] = useState(() => loadSet(HIDDEN_KEY));
  const [hiddenAccounts, setHiddenAccounts] = useState(() => loadSet(HIDDEN_ACCOUNTS_KEY));
  const [calendarsDrawerOpen, setCalendarsDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CalEventDraft | null>(null);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** Tip 137/138: in-app confirm — mobile Safari/PWA often blocks window.confirm. */
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  /** Tip 138 guest: also PATCH RSVP → declined before remove-for-me. */
  const [guestDeclineAlso, setGuestDeclineAlso] = useState(true);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  function choosePhonePane(next: PhonePaneId) {
    setPhonePane(next);
    try {
      localStorage.setItem(PHONE_PANE_KEY, next);
    } catch {
      /* ignore */
    }
  }

  function persistDetailSplit(pct: number) {
    const next = clampDetailSplit(pct);
    setDetailSplitPct(next);
    try {
      localStorage.setItem(DETAIL_SPLIT_KEY, String(next));
    } catch {
      /* ignore */
    }
  }

  function onDetailResizePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    detailResizeRef.current = { startY: event.clientY, startPct: detailSplitPct };
    document.documentElement.dataset.calResizing = "1";

    const onMove = (ev: PointerEvent) => {
      const drag = detailResizeRef.current;
      if (!drag) return;
      const avail = phoneCalAvailHeight();
      const dy = drag.startY - ev.clientY; // drag up → taller detail
      setDetailSplitPct(clampDetailSplit(drag.startPct + (dy / avail) * 100));
    };
    const onUp = (ev: PointerEvent) => {
      handle.releasePointerCapture(ev.pointerId);
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onUp);
      handle.removeEventListener("pointercancel", onUp);
      detailResizeRef.current = null;
      delete document.documentElement.dataset.calResizing;
      setDetailSplitPct((pct) => {
        try {
          localStorage.setItem(DETAIL_SPLIT_KEY, String(pct));
        } catch {
          /* ignore */
        }
        return pct;
      });
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp);
    handle.addEventListener("pointercancel", onUp);
  }

  useEffect(() => {
    const mq = window.matchMedia(PHONE_VIEW_QUERY);
    const apply = () => setView(readSavedView());
    mq.addEventListener("change", apply);
    window.addEventListener("atw-hub-cal-view", apply);
    return () => {
      mq.removeEventListener("change", apply);
      window.removeEventListener("atw-hub-cal-view", apply);
    };
  }, []);

  useEffect(() => {
    function syncWeekStart() {
      const next = readWeekStart();
      setWeekStart(next);
      setAnchor((prev) => startOfWeek(prev, next));
    }
    window.addEventListener("atw-hub-cal-week-start", syncWeekStart);
    window.addEventListener("storage", syncWeekStart);
    return () => {
      window.removeEventListener("atw-hub-cal-week-start", syncWeekStart);
      window.removeEventListener("storage", syncWeekStart);
    };
  }, []);

  useEffect(() => {
    function openDrawer() {
      setCalendarsDrawerOpen(true);
    }
    window.addEventListener("atw-hub-open-calendar-drawer", openDrawer);
    return () => window.removeEventListener("atw-hub-open-calendar-drawer", openDrawer);
  }, []);

  const range = useMemo(() => calendarRange(anchor, view, weekStart), [anchor, view, weekStart]);
  const rangeKey = calendarCacheKey(range);
  const rangeKeyRef = useRef(rangeKey);
  rangeKeyRef.current = rangeKey;

  const load = useCallback(
    async (opts?: { quiet?: boolean }) => {
      const key = calendarCacheKey(range);
      const quiet = Boolean(opts?.quiet);
      let hasData = quiet;
      if (!quiet) {
        setError("");
        setNeedsReconnect(false);
        const saved = await readModuleCache<CalendarSnapshot>(key);
        if (rangeKeyRef.current !== key) return;
        if (saved) {
          setEvents(saved.events);
          setSources(saved.sources);
          hasData = true;
        }
        setLoading(!saved);
      }
      try {
        const snapshot = await fetchCalendarSnapshot(range.from, range.to);
        void writeModuleCache(key, snapshot, { mirror: true }).then(() =>
          pruneModuleCache(CALENDAR_CACHE_PREFIX, CALENDAR_RANGES_CACHED),
        );
        if (rangeKeyRef.current !== key) return;
        setEvents(snapshot.events);
        setSources(snapshot.sources);
        setError("");
        setNeedsReconnect(false);
        if (!quiet) void prefetchNeighborRanges(anchor, view, weekStart);
      } catch (err) {
        if (rangeKeyRef.current !== key) return;
        const authFail = isCalendarAuthError(err);
        // Saved events stay on screen; only renewing access or a cold failure needs a banner.
        if (authFail || !hasData) {
          setNeedsReconnect(authFail);
          setError(
            authFail
              ? "Calendar access needs to be renewed. Reconnect Google Calendar, then refresh."
              : err instanceof Error
                ? err.message
                : "Could not load calendar.",
          );
        }
        if (!hasData) {
          setEvents([]);
          setSources([]);
        }
      } finally {
        if (rangeKeyRef.current === key) setLoading(false);
      }
    },
    [range, anchor, view, weekStart],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useLiveRefresh(() => void load({ quiet: true }), CALENDAR_REFRESH_MS);

  function patchSavedRange(update: (saved: CalEvent[]) => CalEvent[]) {
    const key = rangeKeyRef.current;
    const saved = peekModuleCache<CalendarSnapshot>(key);
    if (saved) void writeModuleCache(key, { ...saved, events: update(saved.events) }, { mirror: true });
  }

  useEffect(() => {
    function onNew() {
      setNotice("New Event — picker arrives in a follow-up tip. Use Google Calendar for now.");
    }
    window.addEventListener("atw-hub-new-event", onNew);
    return () => window.removeEventListener("atw-hub-new-event", onNew);
  }, []);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest(".cal-view-wrap")) return;
      setViewOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const groups = useMemo(() => buildGroups(sources, events), [sources, events]);

  const isShown = useCallback(
    (ev: CalEvent) => {
      const accountId = ev.accountId || ev.accountEmail || "default";
      if (hiddenAccounts.has(accountId)) return false;
      const calendarId = ev.calendarId || "primary";
      return !hiddenSources.has(`${accountId}::${calendarId}`);
    },
    [hiddenAccounts, hiddenSources],
  );

  const searchQuery = useHubSearchQuery("calendar").trim();
  const searchTerm = useDebouncedValue(searchQuery, 400);
  /** Events Google matched for the last search term, across the whole search window. */
  const [searchPool, setSearchPool] = useState<{ term: string; events: CalEvent[] } | null>(null);
  const [searchError, setSearchError] = useState("");
  /** A search result picked from another week: select it once that week's events arrive. */
  const [pendingSelectId, setPendingSelectId] = useState<string | null>(null);

  useEffect(() => {
    if (!searchTerm) {
      setSearchPool(null);
      setSearchError("");
      setPendingSelectId(null);
      return;
    }
    let cancelled = false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    fetchCalendarSnapshot(
      addDays(today, -CALENDAR_SEARCH_BACK_DAYS),
      addDays(today, CALENDAR_SEARCH_AHEAD_DAYS),
      searchTerm,
    )
      .then((snapshot) => {
        if (cancelled) return;
        setSearchPool({ term: searchTerm, events: snapshot.events });
        setSearchError("");
      })
      .catch((err) => {
        if (cancelled) return;
        setSearchPool({ term: searchTerm, events: [] });
        setSearchError(err instanceof Error ? err.message : "Calendar search failed.");
      });
    return () => {
      cancelled = true;
    };
  }, [searchTerm]);

  const searchResults = useMemo(() => {
    if (!searchQuery) return [];
    const byId = new Map<string, CalEvent>();
    for (const ev of [...(searchPool?.events || []), ...events]) {
      if (isShown(ev) && calendarEventMatchesSearch(ev, searchQuery)) byId.set(ev.id, ev);
    }
    return [...byId.values()]
      .sort((a, b) => (a.start || "").localeCompare(b.start || ""))
      .slice(0, 100);
  }, [searchQuery, searchPool, events, isShown]);

  const visibleEvents = useMemo(
    () => events.filter((ev) => isShown(ev) && (!searchQuery || calendarEventMatchesSearch(ev, searchQuery))),
    [events, isShown, searchQuery],
  );

  useEffect(() => {
    if (pendingSelectId && visibleEvents.some((ev) => ev.id === pendingSelectId)) {
      setSelectedId(pendingSelectId);
      setPendingSelectId(null);
    }
  }, [pendingSelectId, visibleEvents]);

  function openSearchResult(ev: CalEvent) {
    if (ev.start) {
      const start = new Date(ev.start);
      if (!Number.isNaN(start.getTime())) {
        const next = anchorForDate(view, weekStart, start);
        setAnchor((prev) => (prev.getTime() === next.getTime() ? prev : next));
      }
    }
    setPendingSelectId(ev.id);
  }

  // Tip 131: only show detail after an explicit selection (no auto-pick of first event).
  const selected = useMemo(() => {
    if (!selectedId) return null;
    return visibleEvents.find((e) => e.id === selectedId) || null;
  }, [visibleEvents, selectedId]);

  useEffect(() => {
    if (selectedId && !visibleEvents.some((e) => e.id === selectedId)) {
      setSelectedId(null);
    }
  }, [visibleEvents, selectedId]);

  useEffect(() => {
    setEditing(false);
    setDraft(null);
    setSaveError("");
    setDeleting(false);
    setDeleteConfirmOpen(false);
  }, [selected?.id]);

  function beginEdit() {
    if (!selected) return;
    setDraft(draftFromEvent(selected));
    setSaveError("");
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setDraft(null);
    setSaveError("");
  }

  function requestDeleteSelected() {
    if (!selected || deleting) return;
    setSaveError("");
    setGuestDeclineAlso(defaultDeclineAlso(selected, selected.accountEmail));
    setDeleteConfirmOpen(true);
  }

  function humanizeCalendarWriteError(err: unknown, fallback: string): string {
    if (!(err instanceof Error)) return fallback;
    const raw = err.message || "";
    if (/cannot\s+(delete|patch|post|put)\s+\/api\/calendar/i.test(raw) || raw.includes("<!DOCTYPE")) {
      return "Calendar delete is not enabled on the Hub API yet (Lightsail). Nothing was removed from Google.";
    }
    return raw || fallback;
  }

  async function tryCalendarWriteAttempts(
    attempts: { path: string; init: RequestInit }[],
  ): Promise<{ ok: true } | { ok: false; err: unknown }> {
    let lastErr: unknown;
    for (const attempt of attempts) {
      try {
        await api(attempt.path, attempt.init);
        return { ok: true };
      } catch (err) {
        lastErr = err;
        if (err instanceof ApiError && err.status !== 404 && err.status !== 405 && err.status !== 501) {
          return { ok: false, err };
        }
      }
    }
    return { ok: false, err: lastErr };
  }

  async function confirmDeleteSelected() {
    if (!selected || deleting) return;
    setDeleting(true);
    setSaveError("");
    const qs = new URLSearchParams();
    if (selected.accountId) qs.set("accountId", selected.accountId);
    if (selected.calendarId) qs.set("calendarId", selected.calendarId);
    const q = qs.toString() ? `?${qs}` : "";
    const id = encodeURIComponent(selected.id);
    const removedId = selected.id;
    const accountEmail = selected.accountEmail || "";
    const owner = isEventOrganizer(selected, accountEmail);
    const markDeclined = !owner && guestDeclineAlso;

    if (markDeclined) {
      const rsvpBody = JSON.stringify({
        responseStatus: "declined",
        status: "declined",
        accountId: selected.accountId,
        calendarId: selected.calendarId,
        email: accountEmail,
      });
      await tryCalendarWriteAttempts([
        {
          path: `/api/calendar/events/${id}/rsvp${q}`,
          init: { method: "PATCH", body: rsvpBody },
        },
        {
          path: `/api/calendar/events/${id}/rsvp${q}`,
          init: { method: "POST", body: rsvpBody },
        },
        {
          path: `/api/calendar/events/rsvp${q}`,
          init: {
            method: "POST",
            body: JSON.stringify({
              id: selected.id,
              eventId: selected.id,
              responseStatus: "declined",
              accountId: selected.accountId,
              calendarId: selected.calendarId,
              email: accountEmail,
            }),
          },
        },
      ]);
      // RSVP is best-effort — still try remove-for-me even if decline route is missing.
    }

    const selfScopeBody = {
      id: selected.id,
      eventId: selected.id,
      accountId: selected.accountId,
      calendarId: selected.calendarId,
      scope: owner ? "all" : "self",
    };
    const attempts: { path: string; init: RequestInit }[] = owner
      ? [
          { path: `/api/calendar/events/${id}${q}`, init: { method: "DELETE" } },
          {
            path: `/api/calendar/events/${id}/delete${q}`,
            init: { method: "POST", body: JSON.stringify({ scope: "all" }) },
          },
          {
            path: `/api/calendar/events/delete${q}`,
            init: { method: "POST", body: JSON.stringify(selfScopeBody) },
          },
        ]
      : [
          {
            path: `/api/calendar/events/${id}/delete${q}`,
            init: {
              method: "POST",
              body: JSON.stringify({ scope: "self" }),
            },
          },
          {
            path: `/api/calendar/events/delete${q}`,
            init: {
              method: "POST",
              body: JSON.stringify({ ...selfScopeBody, scope: "self" }),
            },
          },
          {
            path: `/api/calendar/events/delete${q}`,
            init: {
              method: "POST",
              body: JSON.stringify({ ...selfScopeBody, scope: "attendee" }),
            },
          },
          {
            path: `/api/calendar/events/${id}${q}${q ? "&" : "?"}scope=self`,
            init: { method: "DELETE" },
          },
          // Last resort: plain DELETE (may cancel-for-all on some API builds)
          { path: `/api/calendar/events/${id}${q}`, init: { method: "DELETE" } },
        ];

    const result = await tryCalendarWriteAttempts(attempts);
    if (result.ok) {
      setEvents((prev) => prev.filter((ev) => ev.id !== removedId));
      patchSavedRange((saved) => saved.filter((ev) => ev.id !== removedId));
      setEditing(false);
      setDraft(null);
      setDeleteConfirmOpen(false);
      setSelectedId(null);
      setNotice(owner ? "Event deleted for everyone." : "Removed from your calendar.");
      setDeleting(false);
      return;
    }
    const lastErr = result.err;
    setDeleteConfirmOpen(false);
    const msg =
      lastErr instanceof ApiError &&
      (lastErr.status === 404 || lastErr.status === 405 || lastErr.status === 501)
        ? "Calendar delete is not enabled on the Hub API yet. Nothing was removed from Google or Outlook."
        : humanizeCalendarWriteError(lastErr, "Could not delete event.");
    setSaveError(msg);
    setNotice(msg);
    setDeleting(false);
  }

  async function saveEdit() {
    if (!selected || !draft) return;
    setSaving(true);
    setSaveError("");
    const title = draft.title.trim() || "(no title)";
    const startIso = draft.allDay
      ? draft.start.slice(0, 10) || selected.start || ""
      : draft.start
        ? fromDatetimeLocalValue(draft.start)
        : selected.start || "";
    const endIso = draft.allDay
      ? draft.end.slice(0, 10) || startIso
      : draft.end
        ? fromDatetimeLocalValue(draft.end)
        : selected.end || startIso;
    const body = {
      title,
      summary: title,
      start: startIso,
      end: endIso,
      allDay: draft.allDay,
      location: draft.location.trim(),
      description: draft.description,
      accountId: selected.accountId,
      calendarId: selected.calendarId,
    };
    const qs = new URLSearchParams();
    if (selected.accountId) qs.set("accountId", selected.accountId);
    if (selected.calendarId) qs.set("calendarId", selected.calendarId);
    const path = `/api/calendar/events/${encodeURIComponent(selected.id)}${qs.toString() ? `?${qs}` : ""}`;
    try {
      const updated = await api<CalEvent & { event?: CalEvent; ok?: boolean }>(path, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      const next = normalizeCalendarEvent({
        ...selected,
        ...body,
        ...(updated.event || updated),
        id: selected.id,
      });
      setEvents((prev) => prev.map((ev) => (ev.id === selected.id ? { ...ev, ...next } : ev)));
      patchSavedRange((saved) => saved.map((ev) => (ev.id === selected.id ? { ...ev, ...next } : ev)));
      setEditing(false);
      setDraft(null);
      setNotice("Event updated.");
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 405 || err.status === 501)) {
        setSaveError(
          "Event update is not available on the calendar API yet. Your edits were not saved.",
        );
      } else {
        setSaveError(err instanceof Error ? err.message : "Could not save event.");
      }
    } finally {
      setSaving(false);
    }
  }

  const dayColumns = useMemo(() => {
    if (view === "month") return [];
    const days = viewSpanDays(view);
    const cols: { date: Date; events: CalEvent[] }[] = [];
    for (let i = 0; i < days; i++) {
      const date = addDays(range.from, i);
      const dayEvents = visibleEvents.filter((ev) => eventTouchesDay(ev, date));
      cols.push({ date, events: dayEvents });
    }
    return cols;
  }, [range.from, view, visibleEvents]);

  const todayVisible = dayColumns.some((col) => sameDay(col.date, now));
  const hours = useMemo(
    () => hourWindow(visibleEvents, todayVisible ? now : null),
    [visibleEvents, todayVisible, now],
  );
  const gridMinutes = (hours.max - hours.min) * 60;

  const monthCells = useMemo(() => {
    if (view !== "month") return [];
    const cells: { date: Date; inMonth: boolean; events: CalEvent[] }[] = [];
    let cursor = new Date(range.from);
    while (cursor < range.to) {
      const date = new Date(cursor);
      cells.push({
        date,
        inMonth: date.getMonth() === anchor.getMonth(),
        events: visibleEvents.filter((ev) => eventTouchesDay(ev, date)),
      });
      cursor = addDays(cursor, 1);
    }
    return cells;
  }, [anchor, range.from, range.to, view, visibleEvents]);

  const monthDowLabels = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(startOfWeek(new Date(), weekStart), i);
      return d.toLocaleDateString("en-US", { weekday: "short" });
    });
  }, [weekStart]);

  function toggleAccount(accountId: string) {
    setHiddenAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      saveSet(HIDDEN_ACCOUNTS_KEY, next);
      return next;
    });
  }

  function toggleSource(key: string) {
    setHiddenSources((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      saveSet(HIDDEN_KEY, next);
      return next;
    });
  }

  function shiftRange(dir: -1 | 1) {
    setAnchor((prev) => shiftAnchor(prev, view, dir));
  }

  function goToday() {
    setAnchor(todayAnchor(view, weekStart));
  }

  function renderTimedEvent(layout: TimedLayout) {
    const ev = layout.event;
    if (!ev.start) return null;
    const start = new Date(ev.start);
    const end = new Date(ev.end || ev.start);
    const startMin = start.getHours() * 60 + start.getMinutes() - hours.min * 60;
    let endMin = end.getHours() * 60 + end.getMinutes() - hours.min * 60;
    if (endMin <= startMin) endMin = startMin + 15;
    const colW = 100 / Math.max(layout.columnCount, 1);
    const bg = colorFor(sourceKey(ev), ev.color);
    const active = selected?.id === ev.id;
    const topPx = (startMin / 60) * HOUR_PX;
    const heightPx = Math.max(((endMin - startMin) / 60) * HOUR_PX - 2, 2);
    return (
      <button
        key={ev.id}
        type="button"
        className={active ? "cal-timed-event active" : "cal-timed-event"}
        style={{
          top: topPx,
          height: heightPx,
          left: `calc(${layout.column * colW}% + 2px)`,
          width: `calc(${colW}% - 4px)`,
          background: softColor(bg, 0.32),
          borderLeft: `3px solid ${bg}`,
        }}
        onClick={() => setSelectedId(ev.id)}
        title={eventTitle(ev)}
      >
        <strong>{eventTitle(ev)}</strong>
        <span>{formatShortTime(ev.start)}</span>
      </button>
    );
  }

  function renderCalendarsList(idPrefix = "cal") {
    return (
      <>
        <div className="cal-list-head">
          <strong>All Calendars</strong>
          <p className="muted">Show or hide accounts and calendars. Colors appear on the grid.</p>
        </div>

        {groups.length === 0 && !loading ? (
          <p className="muted pad">No Google calendars loaded yet. Try Refresh.</p>
        ) : null}

        {groups.map((group) => {
          const accountOn = !hiddenAccounts.has(group.accountId);
          const accountInputId = `${idPrefix}-account-${group.accountId}`;
          return (
            <section key={group.accountId} className="cal-account">
              <div className="cal-account-toggle">
                <input
                  id={accountInputId}
                  type="checkbox"
                  checked={accountOn}
                  onChange={() => toggleAccount(group.accountId)}
                />
                <label
                  className="cal-account-name"
                  htmlFor={accountInputId}
                  title={group.accountEmail}
                >
                  {group.accountEmail}
                </label>
              </div>
              <ul className={accountOn ? "cal-source-list" : "cal-source-list dimmed"}>
                {group.calendars.map((cal) => {
                  const on = accountOn && !hiddenSources.has(cal.key);
                  const sourceInputId = `${idPrefix}-source-${cal.key}`;
                  return (
                    <li key={cal.key} className="cal-source-item">
                      <input
                        id={sourceInputId}
                        type="checkbox"
                        checked={on}
                        disabled={!accountOn}
                        onChange={() => toggleSource(cal.key)}
                      />
                      <label
                        className="cal-source-name"
                        htmlFor={sourceInputId}
                        title={cal.title}
                      >
                        {cal.label}
                      </label>
                      <span className="cal-swatch-chip" style={{ background: cal.color }} />
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </>
    );
  }

  const phoneSplitStyle = {
    ["--cal-phone-detail-pct" as string]: String(detailSplitPct),
  } as CSSProperties;

  return (
    <div
      className="cal-layout"
      data-phone-surface={phonePane}
      data-has-selection={selected ? "true" : "false"}
      style={selected ? phoneSplitStyle : undefined}
    >
      <aside className="cal-nav-pane desktop-only" aria-label="All calendars">
        <button
          type="button"
          className="mail-compose-btn cal-new-event"
          onClick={() =>
            setNotice("New Event — picker arrives in a follow-up tip. Use Google Calendar for now.")
          }
        >
          <CalendarPlus size={16} />
          New Event
        </button>

        {renderCalendarsList("cal-desk")}
      </aside>
      <PaneResizer id="calendar.nav" cssVar="--cal-nav-w" min={200} max={400} label="Resize calendars" />

      <section className="cal-main-pane" data-pane-main>
        <div className="cal-toolbar">
          {/* Tip 134 phone: row1 = date + Calendar/List; row2 = Week + Today nav + Refresh */}
          <div className="cal-toolbar-row cal-toolbar-row-primary">
            <div className="cal-range-label">
              <Calendar size={16} className="cal-range-icon" aria-hidden />
              <strong>
                {view === "month"
                  ? anchor.toLocaleDateString("en-US", { month: "long", year: "numeric" })
                  : formatRange(range.from, addDays(range.to, -1))}
              </strong>
            </div>
            <div className="cal-phone-surface-toggle phone-only" role="group" aria-label="Calendar or list">
              <button
                type="button"
                className={phonePane === "calendar" ? "active" : ""}
                aria-pressed={phonePane === "calendar"}
                onClick={() => choosePhonePane("calendar")}
              >
                <Calendar size={14} aria-hidden />
                Calendar
              </button>
              <button
                type="button"
                className={phonePane === "list" ? "active" : ""}
                aria-pressed={phonePane === "list"}
                onClick={() => choosePhonePane("list")}
              >
                <LayoutList size={14} aria-hidden />
                List
              </button>
            </div>
          </div>
          <div className="cal-toolbar-controls">
            <div className="cal-view-wrap">
              <button
                type="button"
                className="mail-sort-trigger"
                aria-expanded={viewOpen}
                onClick={() => setViewOpen((v) => !v)}
              >
                {VIEW_OPTIONS.find((v) => v.id === view)?.label || "Week"}
                <ChevronDown size={14} />
              </button>
              {viewOpen ? (
                <div className="cal-view-menu" role="listbox" aria-label="Calendar view">
                  {VIEW_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      role="option"
                      aria-selected={view === opt.id}
                      className={view === opt.id ? "mail-sort-option active" : "mail-sort-option"}
                      onClick={() => {
                        setView(opt.id);
                        saveSavedView(opt.id);
                        setViewOpen(false);
                      }}
                    >
                      {opt.label}
                      {view === opt.id ? " ✓" : ""}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="cal-nav-group" role="group" aria-label="Date navigation">
              <button type="button" className="icon-btn" aria-label="Previous" onClick={() => shiftRange(-1)}>
                <ChevronLeft size={18} />
              </button>
              <span className="cal-nav-dot" aria-hidden>
                ·
              </span>
              <button type="button" className="cal-today-btn" onClick={goToday}>
                Today
              </button>
              <span className="cal-nav-dot" aria-hidden>
                ·
              </span>
              <button type="button" className="icon-btn" aria-label="Next" onClick={() => shiftRange(1)}>
                <ChevronRight size={18} />
              </button>
            </div>
            <button type="button" className="icon-btn" aria-label="Refresh" onClick={() => void load()}>
              <RefreshCw size={18} />
            </button>
          </div>
        </div>

        {notice ? <p className="banner-info">{notice}</p> : null}
        {error ? (
          <p className="banner-error" role="alert">
            {error}{" "}
            <Link
              className="banner-reconnect-link"
              to={calendarReconnectPath(sources[0]?.accountEmail || null)}
            >
              {needsReconnect ? "Reconnect calendar" : "Open Connections"}
            </Link>
          </p>
        ) : null}
        {loading ? <p className="muted pad">Loading calendar…</p> : null}

        {searchQuery ? (
          <section className="hub-search-results" aria-label="Calendar search results">
            <p className="mail-nav-kicker">
              {searchPool?.term !== searchQuery
                ? "Searching calendar…"
                : `${searchResults.length} ${searchResults.length === 1 ? "event matches" : "events match"} “${searchQuery}”`}
            </p>
            {searchError ? <p className="banner-error">{searchError}</p> : null}
            {searchResults.length ? (
              <ul>
                {searchResults.map((ev) => (
                  <li key={`search-${ev.id}`}>
                    <button
                      type="button"
                      className={selected?.id === ev.id ? "active" : undefined}
                      onClick={() => openSearchResult(ev)}
                    >
                      <span
                        className="hub-search-swatch"
                        style={{ background: colorFor(sourceKey(ev), ev.color) }}
                        aria-hidden
                      />
                      <strong>{eventTitle(ev)}</strong>
                      <span className="muted">{formatEventWhen(ev.start, ev.end, ev.allDay)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}

        <div className="cal-phone-grid-surface">
          {view === "month" ? (
            <div className="cal-month-grid" role="grid" aria-label="Calendar month">
              <div className="cal-month-dow-row">
                {monthDowLabels.map((label) => (
                  <div key={label} className="cal-month-dow">
                    {label}
                  </div>
                ))}
              </div>
              <div className="cal-month-body">
                {monthCells.map((cell) => (
                  <div
                    key={cell.date.toISOString()}
                    className={cell.inMonth ? "cal-month-cell" : "cal-month-cell muted-month"}
                  >
                    <strong>{cell.date.getDate()}</strong>
                    <div className="cal-month-events">
                      {cell.events.slice(0, 4).map((ev) => {
                        const bg = colorFor(sourceKey(ev), ev.color);
                        return (
                          <button
                            key={ev.id}
                            type="button"
                            className={
                              selected?.id === ev.id ? "cal-month-event active" : "cal-month-event"
                            }
                            style={{
                              background: softColor(bg, 0.28),
                              borderLeft: `3px solid ${bg}`,
                            }}
                            onClick={() => setSelectedId(ev.id)}
                            title={eventTitle(ev)}
                          >
                            {isAllDayEvent(ev)
                              ? eventTitle(ev)
                              : `${formatShortTime(ev.start)} ${eventTitle(ev)}`}
                          </button>
                        );
                      })}
                      {cell.events.length > 4 ? (
                        <span className="muted">+{cell.events.length - 4} more</span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="cal-grid-scroll">
              {/* All-day strip above timed hours */}
              <div
                className="cal-week-grid timed"
                style={{
                  gridTemplateColumns: `56px repeat(${dayColumns.length}, minmax(0, 1fr))`,
                }}
                role="grid"
                aria-label="Calendar week"
              >
                <div className="cal-week-gutter-head" aria-hidden />
                {dayColumns.map((col) => {
                  const today = sameDay(col.date, new Date());
                  return (
                    <div
                      key={`head-${col.date.toISOString()}`}
                      className={today ? "cal-week-day-head today" : "cal-week-day-head"}
                    >
                      <span>{col.date.toLocaleDateString("en-US", { weekday: "short" })}</span>
                      <strong>{col.date.getDate()}</strong>
                    </div>
                  );
                })}

                <div className="cal-allday-label" aria-hidden>
                  All day
                </div>
                {dayColumns.map((col) => {
                  const allDay = col.events.filter((ev) => isAllDayEvent(ev));
                  return (
                    <div key={`allday-${col.date.toISOString()}`} className="cal-allday-cell">
                      {allDay.map((ev) => {
                        const bg = colorFor(sourceKey(ev), ev.color);
                        return (
                          <button
                            key={ev.id}
                            type="button"
                            className={
                              selected?.id === ev.id
                                ? "cal-allday-event active"
                                : "cal-allday-event"
                            }
                            style={{
                              background: softColor(bg, 0.32),
                              borderLeft: `3px solid ${bg}`,
                            }}
                            onClick={() => setSelectedId(ev.id)}
                            title={eventTitle(ev)}
                          >
                            {eventTitle(ev)}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}

                <div className="cal-hour-gutter" aria-hidden>
                  {hours.labels.map((h) => (
                    <span key={h} style={{ height: HOUR_PX }}>
                      {formatHourLabel(h)}
                    </span>
                  ))}
                </div>

                {dayColumns.map((col) => {
                  const timed = layoutTimedOverlaps(col.events);
                  const nowMin = now.getHours() * 60 + now.getMinutes() - hours.min * 60;
                  const showNow =
                    todayVisible && gridMinutes > 0 && nowMin >= 0 && nowMin <= gridMinutes;
                  return (
                    <div
                      key={`col-${col.date.toISOString()}`}
                      className="cal-day-col"
                      style={{ height: (hours.max - hours.min) * HOUR_PX }}
                    >
                      {hours.labels.map((h) => (
                        <span key={h} className="cal-hour-line" style={{ height: HOUR_PX }} />
                      ))}
                      {timed.map((layout) => renderTimedEvent(layout))}
                      {showNow ? (
                        <div
                          className="cal-now-line"
                          style={{ top: (nowMin / 60) * HOUR_PX }}
                          aria-hidden
                        >
                          {sameDay(col.date, now) ? <span className="cal-now-dot" /> : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <ul className="cal-phone-list phone-only">
          {visibleEvents.slice(0, 40).map((ev) => (
            <li key={ev.id}>
              <button
                type="button"
                className={selected?.id === ev.id ? "cal-phone-row active" : "cal-phone-row"}
                onClick={() => setSelectedId(ev.id)}
              >
                <span
                  className="cal-swatch-chip"
                  style={{
                    background: colorFor(sourceKey(ev), ev.color),
                  }}
                />
                <div>
                  <strong>{eventTitle(ev)}</strong>
                  <p className="muted">
                    {formatEventWhen(ev.start, ev.end, isAllDayEvent(ev))}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </section>
      <PaneResizer
        id="calendar.detail"
        cssVar="--cal-detail-w"
        min={260}
        max={560}
        edge="left"
        label="Resize event details"
      />

      <aside className="cal-detail-pane" aria-label="Event detail">
        {!selected ? (
          <div className="mail-empty-reader">
            <p>Select an event</p>
          </div>
        ) : (
          <>
          {/* Tip 135: phone split grabber — drag up/down to resize 50/50 default */}
          <div
            className="cal-detail-resize phone-only"
            role="separator"
            aria-orientation="horizontal"
            aria-valuemin={DETAIL_SPLIT_MIN}
            aria-valuemax={DETAIL_SPLIT_MAX}
            aria-valuenow={detailSplitPct}
            aria-label="Resize calendar and event detail"
            title="Drag to resize"
            onPointerDown={onDetailResizePointerDown}
            onDoubleClick={() => persistDetailSplit(DETAIL_SPLIT_DEFAULT)}
          >
            <span className="cal-detail-resize-grip" aria-hidden />
          </div>
          <div className="cal-detail">
            <div className="cal-detail-head">
              <span
                className="cal-swatch-chip"
                style={{
                  background: colorFor(sourceKey(selected), selected.color),
                }}
              />
              {(() => {
                const kicker = formatEventCalendarKicker(
                  resolveEventCalendarFields(selected, groups),
                );
                return (
                  <div className="cal-detail-head-text">
                    <span className="cal-detail-calendar-name">{kicker.primary}</span>
                    {kicker.secondary ? (
                      <span className="muted cal-detail-source-email">{kicker.secondary}</span>
                    ) : null}
                  </div>
                );
              })()}
              {/* Tip 133/136: Edit pencil + Delete trash + Close top-right */}
              <div className="cal-detail-head-actions">
                {!editing ? (
                  <>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label="Edit event"
                      title="Edit"
                      disabled={deleting}
                      onClick={beginEdit}
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      type="button"
                      className="icon-btn cal-detail-delete-btn"
                      aria-label="Delete event"
                      title="Delete"
                      disabled={deleting}
                      onClick={requestDeleteSelected}
                    >
                      <Trash2 size={18} />
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  className="icon-btn"
                  aria-label="Close event detail"
                  title="Close"
                  disabled={deleting}
                  onClick={() => {
                    cancelEdit();
                    setSelectedId(null);
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {saveError ? (
              <p className="cal-detail-error" role="alert">
                {saveError}{" "}
                {isCalendarAuthError(new Error(saveError)) ? (
                  <Link
                    className="banner-reconnect-link"
                    to={calendarReconnectPath(selected.accountEmail)}
                  >
                    Reconnect calendar
                  </Link>
                ) : null}
              </p>
            ) : null}

            {(() => {
              const place = resolveEventPlace({
                meetLink: selected.meetLink,
                hangoutLink: selected.hangoutLink,
                conferenceLink: selected.conferenceLink,
                location: selected.location,
                description: eventDescription(selected),
              });
              const join = place.meeting ? (
                <a className="cal-join-btn" href={place.meeting.url} target="_blank" rel="noreferrer">
                  <ConnectionBrandIcon id={place.meeting.brandId} label={place.meeting.label} size={22} />
                  Join with {place.meeting.label}
                </a>
              ) : null;
              return (
                <div
                  className={`cal-detail-actions${
                    editing || place.meeting ? "" : " desktop-only"
                  }`}
                >
                  {editing ? (
                    <>
                      <button
                        type="button"
                        className="primary-btn"
                        disabled={saving}
                        onClick={() => void saveEdit()}
                      >
                        {saving ? "Saving…" : "Save"}
                      </button>
                      <button type="button" className="text-btn" disabled={saving} onClick={cancelEdit}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    join
                  )}
                </div>
              );
            })()}

            {editing && draft ? (
              <div className="cal-detail-form">
                <label>
                  Title
                  <input
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                    autoFocus
                  />
                </label>
                <label className="cal-detail-check">
                  <input
                    type="checkbox"
                    checked={draft.allDay}
                    onChange={(e) => setDraft({ ...draft, allDay: e.target.checked })}
                  />
                  All day
                </label>
                <label>
                  Starts
                  <input
                    type={draft.allDay ? "date" : "datetime-local"}
                    value={draft.allDay ? draft.start.slice(0, 10) : draft.start}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        start: e.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Ends
                  <input
                    type={draft.allDay ? "date" : "datetime-local"}
                    value={draft.allDay ? draft.end.slice(0, 10) : draft.end}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        end: e.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Location
                  <input
                    value={draft.location}
                    onChange={(e) => setDraft({ ...draft, location: e.target.value })}
                    placeholder="Add location"
                  />
                </label>
                <label>
                  Description
                  <textarea
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    placeholder="Notes"
                  />
                </label>
              </div>
            ) : (
              <>
                <h2>{eventTitle(selected)}</h2>
                <p className="cal-detail-when">
                  {formatEventWhen(selected.start, selected.end, isAllDayEvent(selected))}
                </p>

                <div className="cal-detail-section">
                  <span className="cal-detail-label">Location</span>
                  {(() => {
                    const place = resolveEventPlace({
                      meetLink: selected.meetLink,
                      hangoutLink: selected.hangoutLink,
                      conferenceLink: selected.conferenceLink,
                      location: selected.location,
                      description: eventDescription(selected),
                    });
                    if (!place.location) return <p className="cal-detail-empty">No location</p>;
                    return <CalendarEventLocation place={place} />;
                  })()}
                </div>

                <div className="cal-detail-section">
                  <span className="cal-detail-label">Description</span>
                  {eventDescription(selected) ? (
                    descriptionLooksLikeHtml(eventDescription(selected)) ||
                    /&lt;\/?[a-z]/i.test(eventDescription(selected)) ? (
                      <div
                        className="cal-detail-notes cal-detail-html"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeEventHtml(eventDescription(selected)),
                        }}
                      />
                    ) : (
                      <p className="cal-detail-notes cal-detail-value">{eventDescription(selected)}</p>
                    )
                  ) : (
                    <p className="cal-detail-empty">No description</p>
                  )}
                </div>
              </>
            )}

            <div className="cal-detail-section">
              <span className="cal-detail-label">
                <Users size={12} aria-hidden style={{ verticalAlign: "-1px", marginRight: 4 }} />
                Attendees
              </span>
              {eventAttendees(selected).length ? (
                <ul className="cal-detail-list">
                  {eventAttendees(selected).map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              ) : (
                <p className="cal-detail-empty">No attendees</p>
              )}
            </div>

            <div className="cal-detail-section">
              {(() => {
                const place = resolveEventPlace({
                  meetLink: selected.meetLink,
                  hangoutLink: selected.hangoutLink,
                  conferenceLink: selected.conferenceLink,
                  location: selected.location,
                  description: eventDescription(selected),
                });
                return (
                  <>
                    <div className="cal-detail-label-row">
                      <span className="cal-detail-label">
                        <Video size={14} aria-hidden />
                        Meeting link
                      </span>
                      {place.meeting ? (
                        <a
                          className="cal-join-btn"
                          href={place.meeting.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <ConnectionBrandIcon
                            id={place.meeting.brandId}
                            label={place.meeting.label}
                            size={22}
                          />
                          Join with {place.meeting.label}
                        </a>
                      ) : null}
                    </div>
                    {place.meeting ? (
                      <a className="cal-meeting-url" href={place.meeting.url} target="_blank" rel="noreferrer">
                        {place.meeting.url}
                      </a>
                    ) : (
                      <p className="cal-detail-empty">No meeting link</p>
                    )}
                  </>
                );
              })()}
            </div>

            {!editing && eventDetailsLookSparse(selected) ? (
              <p className="cal-detail-reconnect">
                Details look incomplete.{" "}
                <Link
                  className="banner-reconnect-link"
                  to={calendarReconnectPath(selected.accountEmail)}
                >
                  Reconnect calendar
                </Link>
              </p>
            ) : null}

            <div className="cal-detail-section">
              <span className="cal-detail-label">Recurrence</span>
              {eventRecurrenceSummary(selected) ? (
                <p className="cal-detail-value">{eventRecurrenceSummary(selected)}</p>
              ) : (
                <p className="cal-detail-empty">Does not repeat</p>
              )}
            </div>

            <div className="cal-detail-section">
              <span className="cal-detail-label">Reminders</span>
              {eventReminderLines(selected).length ? (
                <ul className="cal-detail-list">
                  {eventReminderLines(selected).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              ) : (
                <p className="cal-detail-empty">No reminders from API</p>
              )}
            </div>

            {selected.htmlLink ? (
              <div className="cal-detail-section">
                <a
                  className="text-btn"
                  href={selected.htmlLink}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: 0 }}
                >
                  <ExternalLink size={14} aria-hidden />
                  Open in Google Calendar
                </a>
              </div>
            ) : null}
          </div>
          </>
        )}
      </aside>

      {deleteConfirmOpen && selected ? (
        <div
          className="cal-confirm-scrim"
          role="presentation"
          onClick={() => {
            if (!deleting) setDeleteConfirmOpen(false);
          }}
        >
          <div
            className="cal-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cal-delete-title"
            onClick={(event) => event.stopPropagation()}
          >
            {(() => {
              const owner = isEventOrganizer(selected, selected.accountEmail);
              const rsvp = viewerResponseStatus(selected, selected.accountEmail);
              return (
                <>
                  <h3 id="cal-delete-title">
                    {owner ? "Delete for everyone?" : "Remove from your calendar?"}
                  </h3>
                  <p className="cal-confirm-body">
                    <strong>{eventTitle(selected)}</strong>
                    <br />
                    {owner
                      ? "This cancels the meeting for all guests and removes it from Hub and the connected calendar. This cannot be undone."
                      : "This removes the event from your Hub calendar only. Other guests keep it."}
                  </p>
                  {!owner ? (
                    <label className="cal-confirm-option">
                      <input
                        type="checkbox"
                        checked={guestDeclineAlso}
                        disabled={deleting}
                        onChange={(e) => setGuestDeclineAlso(e.target.checked)}
                      />
                      <span>
                        Also mark as Not attending
                        {rsvp ? (
                          <span className="cal-confirm-option-hint"> (you: {rsvp})</span>
                        ) : null}
                      </span>
                    </label>
                  ) : null}
                  <div className="cal-confirm-actions">
                    <button
                      type="button"
                      className="secondary-btn"
                      disabled={deleting}
                      onClick={() => setDeleteConfirmOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="danger-btn"
                      disabled={deleting}
                      onClick={() => void confirmDeleteSelected()}
                    >
                      {deleting
                        ? owner
                          ? "Deleting…"
                          : "Removing…"
                        : owner
                          ? "Delete for everyone"
                          : "Remove"}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      ) : null}

      {calendarsDrawerOpen ? (
        <div
          className="module-slide-scrim phone-only"
          onClick={() => setCalendarsDrawerOpen(false)}
        >
          <aside
            className="module-slide-drawer"
            aria-label="Calendars"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="module-slide-drawer-head">
              <strong>Calendars</strong>
              <button
                type="button"
                className="module-slide-close"
                aria-label="Close Calendars menu"
                onClick={() => setCalendarsDrawerOpen(false)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="module-drawer-body">
              <p className="mail-nav-kicker">Calendars</p>
              {renderCalendarsList("cal-drawer")}
            </div>
            <ModuleDrawerAdd
              to="/settings/connections/calendar"
              label="Connect calendar"
              onNavigate={() => setCalendarsDrawerOpen(false)}
            />
          </aside>
        </div>
      ) : null}
    </div>
  );
}
